import React, { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import questionEvaluatorService from '../../services/questionEvaluatorService';
import QuestionFeedback from './QuestionFeedback';
import { getUserFacingServerError } from '../../utils/apiError';

/**
 * QuestionEvaluator — Productivity Tool (REQ-001)
 * Location: frontend/src/components/question-evaluator/QuestionEvaluator.js
 *
 * Stateless evaluator: the user picks Key Decision or Knowledge Gap, enters a
 * question, and we send it to the AI-only backend
 * (/productivity/question/evaluate). We render the verdict, the per-criterion
 * checklist, a reshaped suggestion, and an optional "switch type" hint via
 * the shared QuestionFeedback component.
 * Markup/CSS ported 1:1 from docs/prototypes/productivity-tools/index.html.
 */

const friendlyError = (err) => {
  const detail = err?.response?.data?.detail;
  const serverError = getUserFacingServerError(err?.response?.status, detail);
  if (serverError) return serverError;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (err?.response?.status === 429) return 'You have reached the usage limit. Please try again shortly.';
  return 'Unable to evaluate your question right now. Please try again.';
};

const QuestionEvaluator = () => {
  const [type, setType] = useState('key_decision');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // A result is only valid for the type it was evaluated against, so switching
  // type clears the previous feedback and prompts a fresh Evaluate.
  const handleSetType = (nextType) => {
    if (nextType === type) return;
    setType(nextType);
    setResult(null);
    setError(null);
  };

  const handleEvaluate = async () => {
    const text = question.trim();
    if (!text) {
      setError('Please enter a question to evaluate.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await questionEvaluatorService.evaluate(text, type);
      setResult(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuestion('');
    setResult(null);
    setError(null);
  };

  const isKD = type === 'key_decision';

  return (
    <div className="productivity-page">
      <div className="grid-two">
        {/* LEFT: selector + instructions + input */}
        <div className="card">
          <h3>What are you evaluating?</h3>
          <div className="segmented" role="tablist" aria-label="Question type">
            <button
              className={isKD ? 'active' : ''}
              role="tab"
              aria-selected={isKD}
              onClick={() => handleSetType('key_decision')}
            >
              Key Decision
            </button>
            <button
              className={!isKD ? 'active' : ''}
              role="tab"
              aria-selected={!isKD}
              onClick={() => handleSetType('knowledge_gap')}
            >
              Knowledge Gap
            </button>
          </div>

          <textarea
            className="input"
            id="qeInput"
            aria-label="Enter a question to evaluate"
            style={{ minHeight: 74, marginTop: 12 }}
            value={question}
            maxLength={2000}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter a question to evaluate…"
          />
          <div className="btn-row">
            <button className="btn" onClick={handleEvaluate} disabled={loading}>
              {loading ? 'Evaluating…' : 'Evaluate'}
            </button>
            <button className="btn secondary" onClick={handleClear} disabled={loading}>Clear</button>
          </div>

          {/* instructions: switch with the selector */}
          {isKD ? (
            <div className="instructions">
              <h4 className="instr-title">What makes a strong Key Decision</h4>
              <p className="lead" style={{ fontSize: '.88rem' }}>
                A Key Decision is a choice your team commits to and doesn&apos;t intend to revisit.
                The test: you can phrase it with the word <strong>&ldquo;will.&rdquo;</strong>
              </p>
              <div className="criteria">
                <span className="tag">Uses &quot;will&quot;</span>
                <span className="tag">A real choice</span>
                <span className="tag">A commitment</span>
              </div>
              <p className="example"><span className="good">Good:</span> &quot;Which supplier <strong>will</strong> we choose?&quot;</p>
              <p className="example"><span className="bad">Not a Key Decision:</span> &quot;Which suppliers <strong>can</strong> deliver the part?&quot; — no &quot;will,&quot; so it&apos;s a Knowledge Gap.</p>
              <div className="deliverable-note">Specifications and requirements are neither key decisions nor knowledge gaps, they are deliverables.</div>
            </div>
          ) : (
            <div className="instructions">
              <h4 className="instr-title">What makes a strong Knowledge Gap</h4>
              <p className="lead" style={{ fontSize: '.88rem' }}>
                A Knowledge Gap is something you need to learn to make a Key Decision well. Use a
                broad question — but <strong>never the word &ldquo;will.&rdquo;</strong>
              </p>
              <div className="criteria">
                <span className="tag">Answerable by learning</span>
                <span className="tag">Tied to a decision</span>
                <span className="tag">Not a task</span>
                <span className="tag">Not a choice you can make</span>
                <span className="tag">Never uses &quot;will&quot;</span>
              </div>
              <p className="example"><span className="good">Good:</span> &quot;Which suppliers <strong>can</strong> deliver the robotic arm?&quot;</p>
              <p className="example"><span className="bad">Not a Knowledge Gap:</span> &quot;Which supplier <strong>will</strong> we choose?&quot; — &quot;will&quot; makes it a decision.</p>
              <p className="relationship-note">
                <strong>How they connect:</strong> Answering the Knowledge Gap (&quot;Which suppliers{' '}
                <strong>can</strong> deliver the robotic arm?&quot;) is how you learn enough to make the
                Key Decision (&quot;Which supplier <strong>will</strong> we choose?&quot;). A good Knowledge
                Gap always feeds a Key Decision.
              </p>
              <div className="deliverable-note">Specifications and requirements are neither key decisions nor knowledge gaps, they are deliverables.</div>
            </div>
          )}
        </div>

        {/* RIGHT: feedback */}
        <div>
          {loading && (
            <div className="card feedback-loading">
              <LoadingSpinner size="large" text="Evaluating your question…" />
            </div>
          )}

          {!loading && error && (
            <div className="card feedback-error fade-in">
              <strong>Something went wrong</strong>
              {error}
            </div>
          )}

          {!loading && !error && result && (
            <div className="card fade-in">
              <h3>Feedback</h3>
              <QuestionFeedback result={result} type={type} onSwitchType={handleSetType} />
            </div>
          )}

          {!loading && !error && !result && (
            <div className="card feedback-empty">
              <span>
                Your evaluation will appear here.<br />
                Enter a question, then select <strong>Evaluate</strong>.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionEvaluator;
